import React, { Component } from 'react';
import { Row, Button } from 'react-bootstrap';
import './TemplateSnippet.css';
import { MediaTextsEditor } from './MediaTextsEditor';
import TemplateSnippetBackgroundPicker from './TemplateSnippetBackgroundPicker';
import axios from 'axios';
import config from '../config';

export class TemplateSnippetEditor extends Component {
    constructor(props) {
        super(props);

        this.state = {
            snippet: null,
            backgroundMedia: null,
            modalShow: false,
            isProcessingBackground: false,
            backgroundProcessingStatus: null,
            organisationUUID: this.props.match.params.uuid,
        };
        this.pollingInterval = null;
    }

    componentWillReceiveProps(newProps) {
        if (newProps.snippet) {
            // Check if snippet has a name (indicates it's fully loaded, not initial empty state)
            const isLoaded = newProps.snippet.name && newProps.snippet.name !== '';

            this.setState({
                snippet: newProps.snippet,
                backgroundMedia: newProps.snippet.backgroundMedia || null,
                isProcessingBackground: !isLoaded, // Show loading if snippet not fully loaded yet
            }, () => {
                // Once loaded, check if background is processing
                if (isLoaded && this.state.backgroundMedia &&
                    (this.state.backgroundMedia.flatteningStatus === 'pending' ||
                     this.state.backgroundMedia.flatteningStatus === 'processing')) {
                    this.setState({
                        isProcessingBackground: true,
                        backgroundProcessingStatus: 'processing'
                    });
                    this.startBackgroundPolling();
                } else if (isLoaded) {
                    // Fully loaded, not processing
                    this.setState({ isProcessingBackground: false });
                }
            });
        }
    }

    componentWillUnmount() {
        // Clean up polling interval
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
        }
    }

    handleModalOpen = () => {
        this.setState({
            modalShow: true
        });
    };

    handleModalSave = () => {
        this.setState({
            modalShow: false
        });
    };

    handleModalClose = () => {
        this.setState({
            modalShow: false
        });
    };

    handleSelectBackground = async (backgroundUUID, fileName) => {
        // Call API to update snippet - backend will copy media + text layers
        this.setState({
            isProcessingBackground: true,
            backgroundProcessingStatus: 'copying',
            modalShow: false
        });

        let self = this;

        try {
            // Copy the template media (backend copies file + text layers, no flattening)
            await axios.patch(
                `${config.baseURL}/snippets/${self.state.snippet.uuid}/backgroundmedia`,
                { backgroundMediaUUID: backgroundUUID }
            );

            // Get the snippet with the copied media
            const snippetResponse = await axios.get(
                `${config.baseURL}/snippets/${self.state.snippet.uuid}`
            );

            const copiedMedia = snippetResponse.data.snippet.backgroundMedia;

            if (!copiedMedia || !copiedMedia.uuid) {
                throw new Error('Copied media not found in snippet response');
            }

            // Done! No flattening yet - user can edit and save later
            self.setState({
                isProcessingBackground: false,
                backgroundProcessingStatus: 'complete',
                snippet: snippetResponse.data.snippet,
                backgroundMedia: copiedMedia
            });

        } catch (err) {
            console.error('Error setting background:', err);
            self.setState({
                isProcessingBackground: false,
                backgroundProcessingStatus: 'failed'
            });
        }
    };

    handlePollingComplete = async (newMediaUUID) => {
        // Reload the snippet to get updated backgroundMedia with new UUID
        // By now the backend should have updated the snippet's backgroundMediaUUID
        try {
            const response = await axios.get(`${config.baseURL}/snippets/${this.state.snippet.uuid}`);
            const snippet = response.data.snippet;

            this.setState({
                snippet: snippet,
                backgroundMedia: snippet.backgroundMedia || null,
            });
        } catch (error) {
            console.error('Error reloading snippet after polling complete:', error);
        }
    };

    startBackgroundPolling = () => {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
        }

        const self = this;
        this.pollingInterval = setInterval(async () => {
            try {
                const response = await axios.get(`${config.baseURL}/snippets/${self.state.snippet.uuid}`);
                const snippet = response.data.snippet;
                const status = snippet.backgroundMedia && snippet.backgroundMedia.flatteningStatus;

                self.setState({ backgroundProcessingStatus: status });

                // Check for completion statuses
                if (status === 'complete' || status === 'completed' || !status) {
                    self.setState({
                        isProcessingBackground: false,
                        backgroundProcessingStatus: 'complete',
                        snippet: snippet,
                        backgroundMedia: snippet.backgroundMedia || null
                    });
                    clearInterval(self.pollingInterval);
                    self.pollingInterval = null;
                } else if (status === 'failed' || status === 'error') {
                    console.error('Background processing failed! Status:', status);
                    self.setState({
                        isProcessingBackground: false,
                        backgroundProcessingStatus: 'failed'
                    });
                    clearInterval(self.pollingInterval);
                    self.pollingInterval = null;
                }
            } catch (error) {
                console.error('Error polling snippet status:', error);
            }
        }, 2000);
    };

    render() {
        const { snippet, backgroundMedia, isProcessingBackground, backgroundProcessingStatus } = this.state;

        // Create a mock match object for MediaTextsEditor with the background media
        const editorMatch = {
            params: {
                uuid: this.state.organisationUUID,
                mediaLibraryUUID: backgroundMedia ? backgroundMedia.mediaLibraryUUID : null,
                mediaUUID: backgroundMedia ? backgroundMedia.uuid : null,
            }
        };

        return (
            <Row className="editor-wrapper">
                {/* Background Processing Overlay */}
                {isProcessingBackground && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 9999,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column'
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: 'white',
                                padding: '30px',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}
                        >
                            <i className="fas fa-spinner fa-spin fa-3x" style={{ marginBottom: '15px' }} />
                            <h4>
                                {backgroundProcessingStatus === 'copying'
                                    ? 'Copying template...'
                                    : backgroundProcessingStatus === 'processing'
                                    ? 'Processing...'
                                    : 'Loading...'}
                            </h4>
                        </div>
                    </div>
                )}

                {/* Show MediaTextsEditor if we have a background */}
                {backgroundMedia && backgroundMedia.uuid && backgroundMedia.mediaLibraryUUID ? (
                    <div style={{ width: '100%' }}>
                        <MediaTextsEditor
                            match={editorMatch}
                            media={backgroundMedia}
                            onPollingComplete={this.handlePollingComplete}
                            customMenuHeader={
                                <Button
                                    onClick={this.handleModalOpen}
                                    bsStyle="primary"
                                    style={{ width: '100%' }}
                                >
                                    <i className="fas fa-image" /> Choose Template
                                </Button>
                            }
                            customMenuFooter={
                                <div style={{
                                    padding: '8px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    textAlign: 'center'
                                }}>
                                    <a
                                        href={`${config.playersBaseURL}/mediaserver/media/${this.state.organisationUUID}/${backgroundMedia.mediaLibraryUUID}/${backgroundMedia.uuid}.${backgroundMedia.extension}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#007bff', textDecoration: 'none' }}
                                    >
                                        <i className="fas fa-external-link-alt" /> View Live Version
                                    </a>
                                </div>
                            }
                        />
                    </div>
                ) : !isProcessingBackground ? (
                    // No background selected - show selector
                    <div style={{
                        width: '100%',
                        height: '400px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        flexDirection: 'column',
                        gap: '20px'
                    }}>
                        <i className="fas fa-image" style={{ fontSize: '64px', color: '#ccc' }} />
                        <h3>No Template Selected</h3>
                        <Button onClick={this.handleModalOpen} bsStyle="primary" bsSize="large">
                            <i className="fas fa-image" /> Choose Template
                        </Button>
                    </div>
                ) : null}

                <TemplateSnippetBackgroundPicker
                    modalShow={this.state.modalShow}
                    handleModalClose={this.handleModalClose}
                    handleModalSave={this.handleModalSave}
                    handleSelectBackground={this.handleSelectBackground}
                    organisationUUID={this.state.organisationUUID}
                />
            </Row>
        );
    }
}

export default TemplateSnippetEditor;
